import { datadogRum, type Context, type RumEvent } from '@datadog/browser-rum';
import { getUserState } from '@carvana/core-auth';

import Cookie from './Cookie';
import { Severity } from './datadog.types';
import type { DatadogInterface, DatadogSpan, LogMessage } from './datadog.types';

const APPRAISAL_PATTERN = /\/sell-my-car\/(getoffer|offer|vin-entry)\//;
const HUB_PATTERN = /\/sell-my-car\/(confirmation|hub|plaid-redirect)\//;

const moduleVersions: { [key: string]: string } = {};
const experiments: Context = {};

const MODULE_NAME = 'stc-shell-ui';
const MODULE_VERSION = `${process.env.APP_VERSION}`;
const ALLOWED_TRACING_URLS_BY_ENVIRONMENT: Record<string, string[]> = {
  production: ['https://apik.carvana.io'],
  test: ['https://apik.carvanatech.com']
};
const IMAGE_FOR_YMMT_PATH_PATTERN =
  /^(\/stc\/trades\/api\/v5\/vehicleconfiguration\/imageforYMMT)\/[^/]+\/[^/]+\/[^/]+\/[^/]+\/?$/i;
const NORMALIZED_IMAGE_FOR_YMMT_PATH =
  '/stc/trades/api/v5/vehicleconfiguration/imageforYMMT/:year/:make/:model/:trim';

const getAllowedTracingUrls = () =>
  ALLOWED_TRACING_URLS_BY_ENVIRONMENT[process.env.CARVANA_APP_ENVIRONMENT ?? ''];

const getTracingConfiguration = () => {
  const allowedTracingUrls = getAllowedTracingUrls();

  if (!allowedTracingUrls) {
    return {};
  }

  return {
    allowedTracingUrls,
    propagateTraceBaggage: true,
    traceContextInjection: 'all' as const,
    traceSampleRate: 100
  };
};

const normalizeRumResourceUrl = (resourceUrl: string) => {
  if (!resourceUrl.toLowerCase().includes('/imageforymmt/')) {
    return resourceUrl;
  }

  try {
    const url = new URL(resourceUrl);

    if (IMAGE_FOR_YMMT_PATH_PATTERN.test(url.pathname)) {
      url.pathname = NORMALIZED_IMAGE_FOR_YMMT_PATH;
      return url.toString();
    }
  } catch {
    return resourceUrl;
  }

  return resourceUrl;
};

const prepareRumResourceEvent = (event: RumEvent) => {
  if (event.type === 'resource') {
    event.resource.url = normalizeRumResourceUrl(event.resource.url);
  }
};

const syncDatadogUser = (userId?: string) => {
  const bcid = Cookie.getBcId();

  if (userId || bcid) {
    datadogRum.setUser({
      ...(userId ? { id: userId } : {}),
      ...(bcid ? { bcid } : {})
    });
    return;
  }

  datadogRum.clearUser();
};

const startPageView = (name: string) => {
  datadogRum.startView({
    name,
    service: MODULE_NAME,
    version: MODULE_VERSION
  });
};

type MfeData = {
  name: string;
  version: string;
};

const isMfeData = (mfeData: unknown): mfeData is MfeData =>
  !!mfeData && typeof mfeData === 'object' && 'name' in mfeData && 'version' in mfeData;

const getMfeData = (event: RumEvent) => {
  if (event.context?.mfeData && isMfeData(event.context.mfeData)) {
    const {
      mfeData: { name, version }
    } = event.context;
    return { name, version };
  }

  return undefined;
};

const harvestFeatureFlags = (event: RumEvent) => {
  if ('feature_flags' in event && event.feature_flags) {
    Object.assign(experiments, event.feature_flags);
  }
};

const determineLoadedMfe = (event: RumEvent) => {
  const mfeData = getMfeData(event);

  if (mfeData) {
    return mfeData;
  }

  let name: string | undefined;

  if (APPRAISAL_PATTERN.test(event.view.url)) {
    name = 'stc-appraisal-ui';
  } else if (HUB_PATTERN.test(event.view.url)) {
    name = 'stc-hub-ui';
  }

  if (!name) {
    return undefined;
  }

  return { name, version: moduleVersions[name] };
};

const initializeDataDog = () => {
  const { user } = getUserState();

  datadogRum.init({
    ...getTracingConfiguration(),
    applicationId: 'c0e19456-086b-48eb-b954-ea6abf661dcf',
    beforeSend: event => {
      prepareRumResourceEvent(event);
      harvestFeatureFlags(event);
      const mfeData = getMfeData(event);

      if (mfeData) {
        moduleVersions[mfeData.name] = mfeData.version;
      }

      const loadedMfe = determineLoadedMfe(event);

      if (loadedMfe?.name) {
        event.service = loadedMfe.name;
        event.version = loadedMfe.version;
      }

      event.context = {
        ...event.context,
        moduleVersions,
        experiments: { ...experiments }
      };

      return true;
    },
    clientToken: 'pub1d8c8908f469710592832a49e98cf269',
    defaultPrivacyLevel: 'mask-user-input',
    env: process.env.CARVANA_APP_ENVIRONMENT,
    service: 'stc-shell-ui',
    sessionReplaySampleRate: 100,
    sessionSampleRate: 100,
    site: 'datadoghq.com',
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    trackViewsManually: true,
    version: process.env.APP_VERSION
  });

  syncDatadogUser(user.user_id);

  datadogRum.addAction('Rum Initialized');
};

const userAuthenticated = (userId: string) => {
  syncDatadogUser(userId);
};
const clearAuthenticatedUser = () => {
  syncDatadogUser();
};
const startTrackingEvent = (component: string, name: string) => {
  const start = performance.now();
  return () => {
    const taken = performance.now() - start;
    datadogRum.addAction(`${component}:${name}`, {
      component,
      name,
      taken
    });
  };
};

const log = (message: LogMessage) => {
  if (message.severity >= Severity.Error) {
    datadogRum.addError(message.message, message);
  }
  datadogRum.addAction('LogMessage', message);
};

const logError = (err: unknown, message?: string) =>
  datadogRum.addError(err, {
    message
  });

const recordException = (exception: unknown, properties: object) =>
  datadogRum.addError(exception, properties);

const addAction = (eventName: string, context?: object) => {
  datadogRum.addAction(eventName, context);
};

export class DataDogSpanImpl implements DatadogSpan {
  private readonly measureStart: DOMHighResTimeStamp;

  private readonly start: Date;

  private readonly spanName: string;

  private readonly component: string;

  private measureEnd?: DOMHighResTimeStamp;

  private ended?: Date;

  constructor(component: string, name: string) {
    this.spanName = name;
    this.component = component;
    this.measureStart = performance.now();
    this.start = new Date();
  }

  private defaultParameters() {
    return {
      component: this.component,
      spanName: this.spanName,
      start: this.start
    };
  }

  addEvent(name: string, attributes?: unknown): this {
    const elapsedMs = performance.now() - this.measureStart;
    datadogRum.addAction(`${this.component}:${this.spanName}:${name}`, {
      ...this.defaultParameters(),
      name,
      elapsedMs,
      attributes: attributes ?? {}
    });
    return this;
  }

  addErrorEvent(name: string, attributes?: unknown, severity?: Severity): this {
    const elapsed = performance.now() - this.measureStart;
    datadogRum.addError(`${this.component}:${this.spanName}:${name}`, {
      severity: severity ?? Severity.Error,
      elapsed,
      attributes: attributes ?? {}
    });

    return this;
  }

  end(attributes?: object): void {
    if (this.measureEnd) {
      return;
    }
    this.measureEnd = performance.now();
    this.ended = new Date();
    const elapsedMs = performance.now() - this.measureStart;

    datadogRum.addAction(`${this.component}:${this.spanName}`, {
      ...this.defaultParameters(),
      ended: this.ended,
      elapsedMs,
      ...(attributes || {})
    });
  }
}

const startSpan = (component: string, name: string): DatadogSpan =>
  new DataDogSpanImpl(component, name);

const addVersion = (moduleName: string, version: string) => {
  moduleVersions[moduleName] = version;
};

const addToRumGlobalContext = (key: string, value: object | string) => {
  datadogRum.setGlobalContextProperty(key, value);
};

const getRumGlobalContext = (key: string) => datadogRum.getGlobalContext()[key];

const datadogInterface: DatadogInterface = {
  setAuthenticatedUser: userAuthenticated,
  clearAuthenticatedUser,
  startSpan,
  log,
  logError,
  recordException,
  addAction,
  addVersion,
  addToRumGlobalContext,
  getRumGlobalContext
};

export {
  datadogInterface,
  DatadogInterface,
  DatadogSpan,
  getAllowedTracingUrls,
  getTracingConfiguration,
  initializeDataDog,
  log,
  recordException,
  normalizeRumResourceUrl,
  prepareRumResourceEvent,
  startPageView,
  startSpan,
  startTrackingEvent,
  syncDatadogUser
};
